/**
 * Validates that a URL belongs to a trusted domain before allowing navigation.
 *
 * Prevents open-redirect attacks where a compromised or malicious server
 * response could redirect users to an attacker-controlled site via
 * `window.location.href`.
 *
 * Only HTTPS URLs on explicitly allowlisted domains are permitted.
 */

const TRUSTED_DOMAINS: string[] = [
  "checkout.stripe.com",
  "billing.stripe.com",
  "basescan.org",
];

/**
 * Returns `true` when `url` is a valid HTTPS URL whose hostname exactly
 * matches (or is a subdomain of) one of the trusted domains.
 *
 * Returns `false` for anything else -- malformed strings, non-HTTPS schemes,
 * javascript: URIs, data: URIs, or unrecognised hosts.
 */
export function isTrustedRedirectUrl(url: string): boolean {
  try {
    const parsed = new URL(url);

    // Only allow HTTPS to prevent scheme-based attacks (javascript:, data:, etc.)
    if (parsed.protocol !== "https:") {
      return false;
    }

    const hostname = parsed.hostname.toLowerCase();

    for (const domain of TRUSTED_DOMAINS) {
      // Exact match or valid subdomain (e.g. "pay.checkout.stripe.com")
      if (hostname === domain || hostname.endsWith(`.${domain}`)) {
        return true;
      }
    }

    return false;
  } catch {
    // URL constructor throws on malformed input -- reject it.
    return false;
  }
}

/**
 * Navigates to `url` only if it passes the trusted-domain check.
 * Logs a warning and returns `false` when the redirect is blocked.
 */
export function safeRedirect(url: string): boolean {
  if (isTrustedRedirectUrl(url)) {
    window.location.href = url;
    return true;
  }

  if (process.env.NODE_ENV === 'development') console.warn(
    "[security] Blocked redirect to untrusted URL:",
    url,
  );
  return false;
}
